<script lang="ts" setup>
  import { setModelWithContext, useFormContext } from '@othercommerce/foundation';
  import { watch, computed } from 'vue';
  import type { CountriesDictionary, RegionsDictionary } from '@othercommerce/domain';

  interface Props {
    data: any,
    countries: CountriesDictionary,
    regions: RegionsDictionary
  }

  const props = defineProps<Props>();

  const form = useFormContext();

  // Reset region when a user changes country
  watch(() => form?.data.value.countryCode, () => {
    setModelWithContext('region', form, null);
  });

  const country = computed(() => {
    if (!form) {
      return undefined;
    }
    return form.data.value['countryCode'];
  });

  const countryRegions = computed(() => {
    if (!country.value || !props.regions) return null;

    return props.regions[country.value];
  });

</script>

<template>
  <section class="address-form">
    <div class="address-form__line">
      <SfFormSelect :label="$t('SHOP.COMMON.LABEL.COUNTRY')" :options="countries" name="countryCode" required />
    </div>

    <div class="address-form__line">
      <SfFormInput :label="$t('SHOP.COMMON.LABEL.FIRSTNAME')" name="firstName" required type="text" />
    </div>

    <div class="address-form__line">
      <SfFormInput :label="$t('SHOP.COMMON.LABEL.LASTNAME')" name="lastName" required type="text" />
    </div>

    <div class="address-form__line">
      <SfFormInput :label="$t('SHOP.COMMON.LABEL.COMPANY_NAME')" name="company" type="text" />
    </div>

    <div v-if="$isImplicitCountry(data.countryCode)" class="address-form__line">
      <div class="address-form__street">
        <SfFormInput :label="$t('SHOP.COMMON.LABEL.STREET')" name="street" required type="text" />
      </div>
      <div class="address-form__building-nr">
        <SfFormInput :label="$t('SHOP.COMMON.LABEL.BUILDING_NR')" name="buildingNr" required type="text" />
      </div>
      <div class="address-form__apartment-nr">
        <SfFormInput :label="$t('SHOP.COMMON.LABEL.APARTMENT_NR')" name="apartmentNr" type="text" />
      </div>
    </div>

    <div v-if="$isExplicitCountry(data.countryCode)" class="address-form__line">
      <SfFormInput :label="$t('SHOP.COMMON.LABEL.ADDRESS_FIRST_LINE')" name="firstLine" required type="text" />
    </div>
    <div v-if="$isExplicitCountry(data.countryCode)" class="address-form__line">
      <SfFormInput :label="$t('SHOP.COMMON.LABEL.ADDRESS_SECOND_LINE')" name="secondLine" type="text" />
    </div>

    <div class="address-form__line">
      <div class="address-form__postcode">
        <SfFormInput :label="$t('SHOP.COMMON.LABEL.POSTCODE')" name="postcode" required type="text" />
      </div>
      <div class="address-form__city">
        <SfFormInput :label="$t('SHOP.COMMON.LABEL.CITY')" name="city" required type="text" />
      </div>
    </div>

    <div class="address-form__line">
      <SfFormSelect v-if="countryRegions" :label="$t('SHOP.COMMON.LABEL.REGION')" :options="countryRegions" name="region" />

      <SfFormInput v-else :label="$t('SHOP.COMMON.LABEL.REGION')" name="region" type="text" />
    </div>

    <div class="address-form__line">
      <SfFormInput :label="$t('SHOP.COMMON.LABEL.SHIPPING_EMAIL')" name="email" required type="email" />
    </div>
    <div class="address-form__line">
      <SfFormInput :label="$t('SHOP.COMMON.LABEL.SHIPPING_PHONE')" name="phone" required type="tel" />
    </div>
    <div class="address-form__line">
      <SfFormSwitch :label="$t('SHOP.COMMON.LABEL.SHIPPING_DEFAULT_ADDRESS')" name="default" />
    </div>
  </section>
</template>
